import SidePanel from './sidepanel';

/*
  Turbo-proof form submission for side-panel workflows.

  This app boots BOTH @rails/ujs and @hotwired/turbo (Turbo Drive). A normal
  remote submit is claimed by TWO handlers: @rails/ujs (XHR) drives the panel via
  SidePanel, but Turbo Drive ALSO processes the same submit and, after the
  server's redirect, runs a full-page `turbo:visit` to the target -- escaping the
  panel. That happens even with data-turbo="false" on the form (proven in a real
  browser: the submit still produced a turbo:visit to Review). Layering opt-out
  attributes is therefore unreliable.

  A form marked `data-sidepanel-fetch` opts out of that machinery entirely.
  sidepanel_form.js intercepts its submit, stops propagation so neither
  @rails/ujs nor Turbo can see it, and calls this function to run the request
  itself: one fetch, then drive the panel from the response.

  Response contract (matches SidepanelResponder for XHR requests):
    * success -> `head :ok, location: <next>` (200 + Location, empty body):
      load <next> inside the panel via SidePanel.load.
    * re-render (e.g. validation error, or the next step rendered directly) ->
      200 HTML with no Location: swap it into the panel via updateContent.

  FormData carries file inputs, the Rails authenticity_token, and the _method
  override, so Upload CSV and Paste from Clipboard converge on one code path and
  method: :patch forms work unchanged.
*/
export default function sidepanelFetchSubmit(form) {
  const tokenEl = document.querySelector('meta[name="csrf-token"]');
  const headers = {
    'X-Requested-With': 'XMLHttpRequest',
    Accept: 'text/html, application/xhtml+xml',
  };
  if (tokenEl && tokenEl.content) {
    headers['X-CSRF-Token'] = tokenEl.content;
  }

  return fetch(form.action, {
    method: (form.getAttribute('method') || 'post').toUpperCase(),
    body: new FormData(form),
    credentials: 'same-origin',
    headers,
  }).then((response) => {
    // Honor X-Reload-Parent-On-Close so closing the panel (e.g. Done after an
    // import) refreshes the underlying page/table without a full-page navigation.
    if (response.headers.get('X-Reload-Parent-On-Close')) {
      SidePanel._reloadParentOnClose = true;
    }
    const location = response.headers.get('X-Xhr-Redirect') || response.headers.get('Location');
    if (location) {
      SidePanel.load(location);
      return null;
    }
    return response.text().then((html) => SidePanel.updateContent(html));
  });
}
